/**
 * EntryPointBreakdown — Eva spend for a month split by AI feature (SF-4567).
 *
 * Every Eva AI call is recorded against the feature it came from: chat, the
 * dashboard summary, the conversational survey, chat titles, and features
 * added later (SF-4565). This section shows that split for the platform
 * (overview page) or one company (company page): a bar per feature plus a
 * table with cost, AI calls and share of the month's spend.
 *
 * Data source follows the rest of the Eva pages: the current month prefers the
 * live split (chat-service, near real time) and falls back to the saved monthly
 * figures when live is unavailable; past months use the saved figures only
 * (SF-4566). Spend from before the split existed is one "Untagged" row, never
 * guessed onto a feature, so the rows always add up to the page's total.
 */
import { Card, Empty, Skeleton, Space, Table, Tag, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LiveIndicator } from '../../../components/kpi/LiveIndicator';
import { formatCurrency, formatNumber, formatPercent } from '../../../utils/format';
import type { EntryPointRow } from './entryPointRows';

const { Title } = Typography;

export interface EntryPointBreakdownProps {
  /** Section heading, e.g. "Spend by AI Feature". */
  title: string;
  /** The month shown, "YYYY-MM". */
  period: string;
  rows: EntryPointRow[];
  isLive: boolean;
  loading: boolean;
  failed?: boolean;
}

/** The section: a bar chart of cost per feature plus the table. */
export const EntryPointBreakdown: React.FC<EntryPointBreakdownProps> = ({
  title,
  period,
  rows,
  isLive,
  loading,
  failed = false,
}) => {
  const { token } = theme.useToken();

  const columns: ColumnsType<EntryPointRow> = [
    { title: 'AI Feature', dataIndex: 'label', key: 'label' },
    {
      title: 'Spend',
      dataIndex: 'costUsd',
      key: 'costUsd',
      align: 'right',
      render: (v: string) => formatCurrency(v),
    },
    {
      title: 'AI Calls',
      dataIndex: 'requests',
      key: 'requests',
      align: 'right',
      render: (v: number | null) => formatNumber(v),
    },
    {
      title: 'Share of Spend',
      dataIndex: 'sharePct',
      key: 'sharePct',
      align: 'right',
      render: (v: number | null) => formatPercent(v),
    },
  ];

  const chartData = rows.map((r) => ({ label: r.label, value: Number(r.costUsd) || 0 }));

  let body: React.ReactNode;
  if (loading) {
    body = <Skeleton active paragraph={{ rows: 4 }} />;
  } else if (rows.length === 0) {
    body = (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          failed ? 'AI spend by feature is unavailable right now' : 'No AI usage this month'
        }
      />
    );
  } else {
    body = (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ width: '100%', height: Math.max(120, rows.length * 44) }} aria-hidden="true">
          <ResponsiveContainer>
            <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v: number) => formatCurrency(v)} />
              <YAxis type="category" dataKey="label" width={200} />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Bar dataKey="value" name="Spend" fill={token.colorPrimary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <Table<EntryPointRow>
          size="middle"
          dataSource={rows}
          columns={columns}
          rowKey="entryPoint"
          pagination={false}
        />
      </Space>
    );
  }

  return (
    <section aria-label={title} data-testid="entry-point-breakdown">
      <Space style={{ marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        <Tag color="purple">{period}</Tag>
        {isLive ? (
          <LiveIndicator title="Spend by AI feature reflects live current-month usage" />
        ) : null}
      </Space>
      <Card size="small">{body}</Card>
    </section>
  );
};
